<script>
/**
 * Cabeçalho do painel do cliente em foco, no topo da lateral da `/ia`.
 *
 * Componente BURRO — tudo por prop, sem store e sem `$access`. Mesma disciplina
 * do `ia-insight-card` e do `chat-sidebar`, e é o que o mantém montável sem
 * sessão no teste.
 *
 * ⚠️ Mostra só o que o repo REALMENTE tem sobre o cliente: nome, `#id`, perfil
 * de suitability e se ele é lead ou cliente. O protótipo desenha também NPS,
 * Risco de Evasão e um selo PREMIUM — nenhum dos três tem fonte por cliente na
 * plataforma (o churn que existe é de PROFISSIONAL). Preenchê-los com número
 * inventado num painel que nomeia um cliente de verdade é pior que não
 * mostrá-los; alguém leria "NPS 9,5 do João" e acreditaria.
 *
 * ★ O selo do canto virou LEAD/CLIENTE, que é o que existe: o CRM decide isso
 * por `id_lead` + `status_lead` (ver `resolveClienteTarget`), e o painel recebe
 * o veredito pronto por prop.
 *
 * Mesma regra que o `chat-cliente-escopo.vue` já segue e registra.
 */
import { formatBrokerCode } from '@utils/cliente-codigo/cliente-codigo'

const TAMANHO_CHEVRON = 12
// Espelham os `CLIENTE_MODE_*` do `clienteTarget` do CRM. Copiados e não
// importados de propósito: o painel não deve depender do módulo de CRM só para
// comparar duas strings, e o contrato é a prop, não o util de lá.
const MODO_LEAD = 'lead'
const MODO_CLIENTE = 'cliente'

export default {
  name: 'IaClienteCabecalho',
  props: {
    // `{ name, id, perfil }` — o shape de `user/allClients`.
    cliente: { type: Object, required: true },
    // `'lead'` | `'cliente'` | `''`. Vazio enquanto a resposta não chegou — e
    // aí o selo não aparece, em vez de aparecer errado e corrigir sozinho meio
    // segundo depois.
    modo: { type: String, default: '' },
    navegavel: { type: Boolean, default: false },
  },
  data() {
    return { TAMANHO_CHEVRON }
  },
  computed: {
    // Só o que o CRM sabe responder. Modo desconhecido não vira "Cliente" por
    // padrão: chamar de cliente quem é lead é errar na direção que custa.
    selo() {
      if (this.modo === MODO_LEAD) return { texto: 'Lead', cor: 'warning' }
      if (this.modo === MODO_CLIENTE) return { texto: 'Cliente', cor: 'success' }
      return null
    },
    nome() {
      return this.cliente.name || this.cliente.nome || ''
    },
    /**
     * `12345 · Perfil moderado` — só os pedaços que existem, sem separador solto.
     *
     * ★ CÓDIGO DA CORRETORA, não o `id`. O id é chave interna do banco: o
     * assessor nunca o viu e não cruza com nada. O código é o identificador que
     * ele usa no cadastro, no CRM e no Consolidador.
     *
     * ⚠️ Via `formatBrokerCode`: 37% dos clientes não têm código e 465 guardam a
     * STRING `'None'` — sem ele o assessor leria "None" sob o nome. Ver o
     * cabeçalho do util, que traz a medição de produção.
     */
    detalhe() {
      const partes = []
      const codigo = formatBrokerCode(this.cliente.codigo_corretora)
      if (codigo) partes.push(codigo)
      if (this.cliente.perfil) partes.push(this.cliente.perfil)
      return partes.join(' · ')
    },
  },
  methods: {
    aoClicar() {
      if (!this.navegavel) return
      this.$emit('abrir')
    },
  },
}
</script>

<template>
  <component
    :is="navegavel ? 'button' : 'div'"
    class="ia-cliente-cabecalho"
    :class="{ 'ia-cliente-cabecalho--navegavel': navegavel }"
    :type="navegavel ? 'button' : null"
    @click="aoClicar"
  >
    <span class="ia-cliente-cabecalho__rotulo ia-texto-selo">
      <span>Perfil do cliente</span>
      <!-- ⚠️ `arrow-right` NÃO existe na lib (nome inexistente renderiza vazio,
           sem erro). O `arrow-left` é que aponta para a DIREITA — armadilha já
           registrada no `ia-insight-card` e no `cliente-sidebar`. -->
      <icon-svg
        v-if="navegavel"
        name="arrow-left"
        :size="TAMANHO_CHEVRON"
        color="var(--neutral-600)"
      />
      <!-- Cor e tinta vêm do DS via BaseStatus — o mesmo selo que a plataforma
           usa para estado em outras telas. -->
      <BaseStatus
        v-if="selo"
        class="ia-cliente-cabecalho__selo"
        soft
        :content="selo.texto"
        :color="selo.cor"
      />
    </span>
    <span class="ia-cliente-cabecalho__nome ia-texto-titulo-cartao" :title="nome">{{ nome }}</span>
    <span v-if="detalhe" class="ia-cliente-cabecalho__detalhe ia-texto-auxiliar">
      {{ detalhe }}
    </span>
  </component>
</template>

<style lang="sass" scoped>
.ia-cliente-cabecalho
  display: flex
  flex-direction: column
  align-items: flex-start
  gap: 4px
  width: 100%
  padding: 0
  text-align: left
  background: transparent
  border: 0

.ia-cliente-cabecalho--navegavel
  cursor: pointer

  &:hover .ia-cliente-cabecalho__rotulo
    color: var(--neutral-800)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

// Mesma régua do `.ia-insight__rotulo`: os dois abrem blocos na mesma coluna, e
// tamanhos diferentes fariam a lateral ler como duas telas coladas.
.ia-cliente-cabecalho__rotulo
  color: var(--neutral-600)
  display: flex
  align-items: center
  gap: 4px
  // ⚠️ Largura cheia: o pai é `align-items: flex-start`, então sem isto a linha
  // encolhe até o texto e o `margin-left: auto` do selo não tem para onde
  // empurrar — ele ficaria colado no chevron.
  width: 100%
  // [bug corrigido, feedback do usuário] `modo` chega vazio até o CRM resolver
  // lead/cliente — sem o selo, a linha tinha só a altura do texto (11px); ele
  // aparece um instante depois com `.status-chip--soft` (line-height 20px +
  // padding 4px de cada lado, ~28px) e empurrava TODO o conteúdo abaixo pra
  // baixo. Reservado aqui, não é retângulo cinza — sem selo a linha só fica
  // mais alta, vazia, e o encaixe do selo depois não move mais nada.
  min-height: 28px
  letter-spacing: 0.06em
  text-transform: uppercase

// ★ Maior que qualquer valor de insight (20 contra 17): é o NOME de uma pessoa
// abrindo a coluna, não mais um número — e é o que diz, de relance, que a
// lateral inteira mudou de assunto.
//
// ⚠️ Sem trocar de família. O protótipo usa serifa aqui, mas a plataforma só
// expõe `--font-family-paragraph` como token; um `font-family` literal fugiria
// do design system, e um token inventado não resolve e cai no fallback do
// navegador — que é como este comentário nasceu.
//
// ★ Teto de DUAS linhas (review do PR #2261, I-21): o orçamento de altura do
// `ia-cliente-painel.vue` conta o nome em 2 × 24px; sem o teto, uma razão
// social em 3 linhas estourava o piso de 660px e o `overflow: hidden` do
// painel cortava a última linha de transcrições em silêncio. Mesma receita de
// clamp do módulo (`acoes-por-modulo.vue`). O nome inteiro fica no `title`.
.ia-cliente-cabecalho__nome
  color: var(--neutral-900)
  display: -webkit-box
  -webkit-line-clamp: 2
  -webkit-box-orient: vertical
  overflow: hidden

// Encostado à direita da linha do rótulo, onde o protótipo põe o selo.
.ia-cliente-cabecalho__selo
  margin-left: auto

.ia-cliente-cabecalho__detalhe
  color: var(--neutral-600)
  font-variant-numeric: tabular-nums
</style>
